import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { MeResponse, MeUser } from '@aralere/shared-types';

/** The active brand association's client summary from /api/me (04 #3) — drives the paired header. */
export type ActiveClientSummary = NonNullable<MeResponse['activeClient']>;

interface AuthContextValue {
  user: MeUser | null;
  /** Current brand association's client join date (header shows the year, spec §3.5). Null for non-clients. */
  memberSince: string | null;
  /** The active brand association's client (id, name, type, logo) — null for admins / no association. */
  activeClient: ActiveClientSummary | null;
  /**
   * True when the signed-in user is a platform-admin AND the admin surface is
   * permitted on this host (08 Q10 — from /api/me, gated by ADMIN_HOSTNAME).
   * Drives admin-nav visibility + the platform-admin route guard. The server's
   * withRole is the authority; this only avoids showing admin UI that would 403.
   * False for non-admins and for admins on a non-admin host.
   */
  adminSurface: boolean;
  /** 04 #125 WS-B: any brand carries a pass-through charge class (from /api/me, admins only). Shows
   *  the "Collected for others" nav entry + dashboard card; false hides them. */
  collectsForOthers: boolean;
  loading: boolean;
  error: Error | null;
  /**
   * True when /api/me failed with 403 `session_revoked` — this browser still
   * holds SWA cookies, but the server revoked them ("sign out everywhere",
   * 04 #6). The session CANNOT be recovered via /.auth/login (it short-circuits
   * without Entra and re-mints only the SWA cookie — HAR-proven 2026-06-12);
   * recovery must purge via /.auth/logout. Use `signIn()` below, which routes
   * correctly for both states.
   */
  sessionRevoked: boolean;
  /** THE sign-in action: normal /.auth/login, or the purge path when revoked. */
  signIn: (returnTo?: string) => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  memberSince: null,
  activeClient: null,
  adminSurface: false,
  collectsForOthers: false,
  loading: true,
  error: null,
  sessionRevoked: false,
  signIn: () => {},
  refresh: async () => {},
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<MeUser | null>(null);
  const [memberSince, setMemberSince] = useState<string | null>(null);
  const [activeClient, setActiveClient] = useState<ActiveClientSummary | null>(null);
  const [adminSurface, setAdminSurface] = useState(false);
  const [collectsForOthers, setCollectsForOthers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sessionRevoked, setSessionRevoked] = useState(false);

  const refresh = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // SWA's /.auth/me tells us if we're signed in at all
      const authRes = await fetch('/.auth/me');
      const authData = (await authRes.json()) as { clientPrincipal: unknown | null };
      if (!authData.clientPrincipal) {
        setUser(null);
        setMemberSince(null);
        setActiveClient(null);
        setAdminSurface(false);
        setSessionRevoked(false);
        return;
      }
      // Then /api/me resolves the Cosmos record
      const meRes = await fetch('/api/me');
      if (meRes.status === 401 || meRes.status === 403) {
        setUser(null);
        setMemberSince(null);
        setActiveClient(null);
        setAdminSurface(false);
        setSessionRevoked(await isSessionRevokedResponse(meRes));
        return;
      }
      if (!meRes.ok) throw new Error(`/api/me returned ${meRes.status}`);
      const me = (await meRes.json()) as MeResponse;
      setUser(me.user);
      setMemberSince(me.memberSince ?? null);
      setActiveClient(me.activeClient ?? null);
      setAdminSurface(me.adminSurface ?? false);
      setCollectsForOthers(me.collectsForOthers ?? false);
      setSessionRevoked(false);
    } catch (err) {
      setError(err as Error);
      setUser(null);
      setMemberSince(null);
      setActiveClient(null);
      setAdminSurface(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const signIn = (returnTo?: string): void => {
    if (sessionRevoked) {
      revokedRecoveryRedirect();
    } else {
      loginRedirect(returnTo);
    }
  };

  return (
    <AuthContext.Provider value={{ user, memberSince, activeClient, adminSurface, collectsForOthers, loading, error, sessionRevoked, signIn, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Detect the withAuth 403 `session_revoked` body (shape-tolerant, never throws). */
async function isSessionRevokedResponse(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.json()) as { code?: string; error?: { code?: string } } | null;
    const code = body?.code ?? body?.error?.code ?? null;
    return code === 'session_revoked';
  } catch {
    return false;
  }
}

export function loginRedirect(returnTo?: string): void {
  const target = returnTo ?? window.location.pathname + window.location.search;
  window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(target)}`;
}

/**
 * Sign-out (04 #6, final form 2026-06-12): navigate to the SPA /logout route, the SINGLE
 * sign-out entry point. That route (apps/portal/src/routes/Logout.tsx) first calls
 * POST /api/logout-all — server-side revocation that kills this user's sessions on the
 * OTHER portal and every device (cross-domain cookies can't be cleared from here) — and
 * then runs the local /.auth/logout → /signed-out → Entra end-session chain. The old SWA
 * config redirect for /logout was removed so this route always executes and revocation
 * can never be skipped.
 */
export function logoutRedirect(): void {
  window.location.href = '/logout';
}

/**
 * Recovery for a REVOKED leftover session (04 #6, HAR-proven 2026-06-12):
 * /.auth/login must NOT be used — with a live SWA session it short-circuits
 * (zero Entra contact) and re-mints only StaticWebAppsAuthCookie, which the
 * dual-fingerprint gate still rejects (the AppServiceAuthSession anchor stays
 * pre-stamp). The only way back in is a REAL Entra sign-in, and the path to one
 * is the purge: /.auth/logout deletes the anchor at its first hop; from the
 * resulting half-state, /.auth/logout bounces straight into an Entra authorize
 * (credential prompt). Each click lands somewhere sensible; at most two hops.
 */
export function revokedRecoveryRedirect(): void {
  window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
}
